import { useState, useEffect, useCallback } from "react";
import {
  Users,
  CalendarOff,
  Banknote,
  Clock,
  Building,
  AlertTriangle,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { businessApi } from "../../../services/api";
import { IconButton, PageHeader } from "../../../components/ui";

/* ─── Helpers ───────────────────────────────────────────── */

const fmtCurrency = (cents) => {
  if (cents == null) return "₱0.00";
  return "₱" + (Number(cents) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtPct = (n) => (n == null || isNaN(n) ? "0%" : n.toFixed(1) + "%");

const fmtMinutes = (m) => {
  if (!m) return "0h";
  const h = Math.floor(m / 60);
  const min = m % 60;
  return h > 0 ? `${h}h ${min}m` : `${min}m`;
};

/* ─── Stat Card ─────────────────────────────────────────── */

function StatCard({ icon: Icon, label, value, sub, color = "text-neutral-700 dark:text-neutral-200" }) {
  return (
    <div className="rounded-xl border bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
      <div className="flex items-center gap-3">
        <div className={`rounded-lg bg-neutral-100 p-2 dark:bg-neutral-800 ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-2xl font-bold tracking-tight">{value}</div>
          <div className="text-xs text-neutral-500 dark:text-neutral-400">{label}</div>
        </div>
      </div>
      {sub && <div className="mt-2 text-xs text-neutral-400">{sub}</div>}
    </div>
  );
}

/* ─── Mini Bar (horizontal percentage bar) ──────────────── */

function MiniBar({ items }) {
  const total = items.reduce((s, i) => s + i.value, 0);
  if (total === 0) return <div className="h-3 rounded-full bg-neutral-100 dark:bg-neutral-800" />;
  return (
    <div className="flex h-3 overflow-hidden rounded-full">
      {items.map((item, i) => (
        <div
          key={i}
          className={item.color}
          style={{ width: `${(item.value / total) * 100}%` }}
          title={`${item.label}: ${item.value}`}
        />
      ))}
    </div>
  );
}

/* ─── Donut Chart (CSS-only conic gradient) ─────────────── */

function DonutChart({ segments, size = 140, thickness = 20, centerLabel, centerSub }) {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (total === 0) {
    return (
      <div style={{ width: size, height: size }} className="flex items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-800">
        <span className="text-xs text-neutral-400">No data</span>
      </div>
    );
  }
  let accPct = 0;
  const stops = [];
  for (const seg of segments) {
    const pct = (seg.value / total) * 100;
    stops.push(`${seg.cssColor} ${accPct}% ${accPct + pct}%`);
    accPct += pct;
  }
  const gradient = `conic-gradient(${stops.join(", ")})`;

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <div className="absolute inset-0 rounded-full" style={{ background: gradient }} />
      <div
        className="absolute rounded-full bg-white dark:bg-neutral-900"
        style={{
          top: thickness,
          left: thickness,
          right: thickness,
          bottom: thickness,
        }}
      />
      <div className="relative z-10 text-center">
        {centerLabel && <div className="text-lg font-bold leading-tight">{centerLabel}</div>}
        {centerSub && <div className="text-[10px] text-neutral-400">{centerSub}</div>}
      </div>
    </div>
  );
}

/* ─── Horizontal Labeled Bar ────────────────────────────── */

function LabeledBar({ label, value, maxValue, color = "bg-violet-500", suffix = "" }) {
  const pct = maxValue > 0 ? Math.min((value / maxValue) * 100, 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="text-neutral-600 dark:text-neutral-300 truncate">{label}</span>
        <span className="font-medium tabular-nums">{value}{suffix}</span>
      </div>
      <div className="h-2 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${Math.max(pct, 1)}%` }} />
      </div>
    </div>
  );
}

/* ─── Attendance Sparkline (CSS-only bar chart) ─────────── */

function AttendanceTrend({ data }) {
  if (!data || data.length === 0)
    return <div className="py-8 text-center text-sm text-neutral-400">No attendance data for this period.</div>;

  const maxVal = Math.max(...data.map((d) => d.present + d.absent + d.on_leave), 1);

  return (
    <div className="flex items-end gap-px" style={{ height: 120 }}>
      {data.map((d, i) => {
        const h = ((d.present + d.late) / maxVal) * 100;
        const dayLabel = new Date(d.day + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });
        return (
          <div key={i} className="group relative flex-1 min-w-[4px]">
            <div
              className="w-full rounded-t bg-emerald-500 transition-colors group-hover:bg-emerald-400"
              style={{ height: `${Math.max(h, 2)}%` }}
            />
            <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden rounded bg-neutral-800 px-2 py-1 text-[10px] text-white whitespace-nowrap group-hover:block dark:bg-neutral-700">
              {dayLabel}: {d.present + d.late} present, {d.absent} absent
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Main Component ────────────────────────────────────── */

export default function HRAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await businessApi.getHRAnalytics({ year });
      setData(res.analytics);
    } catch (err) {
      setError(err?.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-16 text-center">
        <AlertTriangle className="mx-auto h-8 w-8 text-amber-500" />
        <p className="mt-2 text-sm text-neutral-500">{error}</p>
        <button onClick={load} className="mt-3 rounded-lg border px-4 py-2 text-sm dark:border-neutral-700">Retry</button>
      </div>
    );
  }

  const { headcount, attendanceRates, attendanceTrend, leaveUsage, payrollCosts, payrollCostsRestricted, departments, topAbsentees } = data || {};

  const totalRecords = Number(attendanceRates?.total_records || 0);
  const presentRate = totalRecords > 0 ? (Number(attendanceRates.present_count) / totalRecords) * 100 : 0;
  const lateRate = totalRecords > 0 ? (Number(attendanceRates.late_count) / totalRecords) * 100 : 0;
  const absentRate = totalRecords > 0 ? (Number(attendanceRates.absent_count) / totalRecords) * 100 : 0;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <PageHeader
        title="HR Analytics"
        description="Workforce insights, attendance trends, leave usage, and payroll overview."
        actions={
          <div className="flex items-center gap-1">
            <IconButton icon={ChevronLeft} label="Previous year" size="sm" variant="secondary" onClick={() => setYear((y) => y - 1)} />
            <span className="min-w-[3.5rem] text-center text-body-sm font-semibold tabular-nums" aria-live="polite">{year}</span>
            <IconButton icon={ChevronRight} label="Next year" size="sm" variant="secondary" onClick={() => setYear((y) => y + 1)} />
          </div>
        }
      />

      {/* ─── Headcount Row ─────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard icon={Users} label="Total Workforce" value={headcount?.total || 0} color="text-blue-600 dark:text-blue-400" />
        <StatCard icon={Users} label="Active" value={headcount?.active || 0} color="text-emerald-600 dark:text-emerald-400" />
        <StatCard icon={Clock} label="On Hold" value={headcount?.on_hold || 0} color="text-amber-600 dark:text-amber-400" />
        <StatCard icon={Users} label="Terminated" value={headcount?.terminated || 0} color="text-red-600 dark:text-red-400" />
      </div>

      {/* ─── Attendance Summary Row ────────────────── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-xl border bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Attendance Rate (This Month)</h3>
          <div className="mt-4 flex items-center gap-5">
            <DonutChart
              size={120}
              thickness={18}
              centerLabel={fmtPct(presentRate)}
              centerSub="present"
              segments={[
                { label: "Present", value: Number(attendanceRates?.present_count || 0), cssColor: "#10b981" },
                { label: "Late", value: Number(attendanceRates?.late_count || 0), cssColor: "#f59e0b" },
                { label: "Absent", value: Number(attendanceRates?.absent_count || 0), cssColor: "#ef4444" },
                { label: "On Leave", value: Number(attendanceRates?.on_leave_count || 0), cssColor: "#8b5cf6" },
              ]}
            />
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center gap-2"><span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500" /> Present: {Number(attendanceRates?.present_count || 0)}</div>
              <div className="flex items-center gap-2"><span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-500" /> Late: {Number(attendanceRates?.late_count || 0)}</div>
              <div className="flex items-center gap-2"><span className="inline-block h-2.5 w-2.5 rounded-full bg-red-500" /> Absent: {Number(attendanceRates?.absent_count || 0)}</div>
              <div className="flex items-center gap-2"><span className="inline-block h-2.5 w-2.5 rounded-full bg-violet-500" /> On Leave: {Number(attendanceRates?.on_leave_count || 0)}</div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Hours Worked (This Month)</h3>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-bold">{fmtMinutes(Number(attendanceRates?.total_worked_minutes || 0))}</span>
          </div>
          <div className="mt-2 flex flex-col gap-1 text-xs text-neutral-500 dark:text-neutral-400">
            <span>Approved overtime (paid): {fmtMinutes(Number(attendanceRates?.total_approved_overtime_minutes || 0))}</span>
            <span>Extra time after shifts: {fmtMinutes(Number(attendanceRates?.total_overtime_minutes || 0))}</span>
            <span>Late: {fmtMinutes(Number(attendanceRates?.total_late_minutes || 0))}</span>
          </div>
        </div>

        <div className="rounded-xl border bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Department Breakdown</h3>
          <div className="mt-3 space-y-2">
            {(departments || []).slice(0, 5).map((d, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <Building className="h-3.5 w-3.5 text-neutral-400" />
                  <span className="truncate">{d.department}</span>
                </div>
                <span className="font-medium">{d.member_count}</span>
              </div>
            ))}
            {(!departments || departments.length === 0) && (
              <div className="text-xs text-neutral-400">No departments configured.</div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Attendance Trend ──────────────────────── */}
      <div className="rounded-xl border bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Attendance Trend (Last 30 Days)</h3>
        <div className="mt-4">
          <AttendanceTrend data={attendanceTrend} />
        </div>
      </div>

      {/* ─── Bottom Grid: Leave + Payroll + Absentees  */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Leave Usage */}
        <div className="rounded-xl border bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900">
          <div className="flex items-center gap-2">
            <CalendarOff className="h-4 w-4 text-violet-500" />
            <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Leave Usage ({year})</h3>
          </div>
          <div className="mt-3 space-y-3">
            {(leaveUsage || []).length === 0 && <div className="text-xs text-neutral-400">No approved leaves this year.</div>}
            {(() => {
              const maxDays = Math.max(...(leaveUsage || []).map((lu) => Number(lu.total_days_taken) || 0), 1);
              return (leaveUsage || []).map((lu, i) => (
                <LabeledBar
                  key={i}
                  label={lu.leave_type}
                  value={Number(lu.total_days_taken).toFixed(1)}
                  maxValue={maxDays}
                  color="bg-violet-500"
                  suffix={`d (${lu.request_count})`}
                />
              ));
            })()}
          </div>
        </div>

        {/* Payroll Costs */}
        <div className="rounded-xl border bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900">
          <div className="flex items-center gap-2">
            <Banknote className="h-4 w-4 text-emerald-500" />
            <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Recent Payroll</h3>
          </div>
          <div className="mt-3 space-y-3">
            {payrollCostsRestricted ? (
              <div className="text-xs text-neutral-400">Payroll totals are shown only to people with payroll access.</div>
            ) : (payrollCosts || []).length === 0 ? (
              <div className="text-xs text-neutral-400">No payroll periods found.</div>
            ) : null}
            {(() => {
              const maxNet = Math.max(...(payrollCosts || []).map((pc) => Number(pc.total_net_cents) || 0), 1);
              return (payrollCosts || []).map((pc, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <div>
                      <div className="text-neutral-600 dark:text-neutral-300 truncate max-w-[140px]">{pc.period_name || `${pc.start_date} – ${pc.end_date}`}</div>
                      <div className="text-[10px] text-neutral-400">{pc.payslip_count} payslips · {pc.status}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{fmtCurrency(pc.total_net_cents)}</div>
                      <div className="text-[10px] text-neutral-400">gross: {fmtCurrency(pc.total_gross_cents)}</div>
                    </div>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-neutral-100 dark:bg-neutral-800">
                    <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, ((Number(pc.total_net_cents) || 0) / maxNet) * 100)}%` }} />
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>

        {/* Top Absentees */}
        <div className="rounded-xl border bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Attention (This Month)</h3>
          </div>
          <div className="mt-3 space-y-3">
            {(topAbsentees || []).length === 0 && <div className="text-xs text-neutral-400">No absences or late arrivals.</div>}
            {(() => {
              const maxTotal = Math.max(...(topAbsentees || []).map((e) => (Number(e.absent_days) || 0) + (Number(e.late_days) || 0)), 1);
              return (topAbsentees || []).map((emp, i) => {
                const absent = Number(emp.absent_days) || 0;
                const late = Number(emp.late_days) || 0;
                const total = absent + late;
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-neutral-600 dark:text-neutral-300 truncate max-w-[140px]">{emp.member_name}</span>
                      <div className="flex items-center gap-2 text-xs">
                        {absent > 0 && <span className="text-red-500">{emp.absent_days} absent</span>}
                        {late > 0 && <span className="text-amber-500">{emp.late_days} late</span>}
                      </div>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-neutral-100 dark:bg-neutral-800 flex overflow-hidden">
                      {absent > 0 && <div className="h-full bg-red-500" style={{ width: `${(absent / maxTotal) * 100}%` }} />}
                      {late > 0 && <div className="h-full bg-amber-400" style={{ width: `${(late / maxTotal) * 100}%` }} />}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}
